import React, { useContext, useEffect } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import Styles from "./AddQus.module.css";
import LoginContext from "./LoginContext";
import { API_BASE_URL } from "../constants/apiConfig";
import { useParams, useNavigate } from "react-router-dom";

interface IFormInput {
  ques: string;
  OptionA: string;
  OptionB: string;
  OptionC: string;
  OptionD: string;
  answer: string;
  subject: string;
  category: string;
  norepetation?: string;
  repeatedyear?: string;
}

const schema = Joi.object({
  ques: Joi.string().required(),
  optionA: Joi.string().required(),
  optionB: Joi.string().required(),
  optionC: Joi.string().required(),
  optionD: Joi.string().required(),
  answer: Joi.number().required(),
  category: Joi.number().required(),
  subject: Joi.number().required(),
});
const AddQus = () => {
  const { setIsLoading, setModelData }: any =
    useContext(LoginContext);
  const { qusId } = useParams<{ qusId: string }>();
  const navigate = useNavigate();
  const isEditMode = !!qusId;
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<IFormInput>({
    resolver: joiResolver(schema),
  });

  // Fetch question data when in edit mode
  useEffect(() => {
    if (isEditMode && qusId) {
      setIsLoading(true);
      fetch(`${API_BASE_URL}/admin/singleQp?qusId=${qusId}`, {
        credentials: "include",
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("Fetched question data:", data);
          if (data.length > 0) {
            console.log("Fetched question for editing:", data);
            const question = data[0];
            // Populate form fields
            setValue("ques", question.question);
            setValue("optionA" as any, question.optionA);
            setValue("optionB" as any, question.optionB);
            setValue("optionC" as any, question.optionC);
            setValue("optionD" as any, question.optionD);
            setValue("answer", question.answer);
            setValue("subject", question.subject);
            setValue("category", question.category);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          setModelData({
            isOpen: true,
            title: "Error",
            message: "Failed to fetch question: " + error.message,
          });
          setIsLoading(false);
        });
    }
  }, [isEditMode, qusId, setIsLoading, setModelData, setValue]);

  const onSubmit = (data: IFormInput) => {
    data.norepetation = "0";
    data.repeatedyear = "0";
    setIsLoading(true);

    const endpoint = isEditMode
      ? `${API_BASE_URL}/admin/updateqp/${qusId}`
      : `${API_BASE_URL}/admin/addqp`;
    const method = isEditMode ? "PUT" : "POST";

    fetch(endpoint, {
      method: method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => {
        setIsLoading(false);
        const modelData = {
          isOpen: true,
          title: isEditMode ? "Update Question" : "Save Question",
          message: data.message,
        };
        setModelData({ ...modelData });
        if (data.status > 0) {
          reset();
          if (isEditMode) {
            setTimeout(() => navigate("/listqus"), 1500);
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setModelData({
          isOpen: true,
          title: "Error",
          message: "Failed to " + (isEditMode ? "update" : "save") + " question: " + error.message,
        });
      });
  };
  return (
    <>
      <form className="flex flex-col gap-4 p-10 max-w-full mx-auto bg-base-100 shadow-xl rounded-box flex-wrap" onSubmit={handleSubmit(onSubmit)}>
  <h2 className="text-xl font-bold">{isEditMode ? "Edit Question" : "Add Question"}</h2>
  
  {/* Question */}
  <div className="form-control">
    <label className="label"><span className="label-text">Question</span></label>
    <textarea  {...register("ques")} className="textarea textarea-bordered" placeholder="..."></textarea>
    <ErrorMessage
              errors={errors}
              name={"ques"}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
  </div>

  {/* Options */}
  <div className="flex flex-col gap-2">
    <input {...register("optionA" as any)} key={"optionA"} type="text" placeholder="Option A" className="input input-bordered" />
      <ErrorMessage
              errors={errors}
              name={"optionA" as any}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
       
      <input {...register("optionB" as any)} key={"optionB"} type="text" placeholder="Option B" className="input input-bordered" />
       <ErrorMessage
              errors={errors}
              name={"optionB" as any}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
        <input {...register("optionC" as any)} key={"optionC"} type="text" placeholder="Option C" className="input input-bordered" />
       <ErrorMessage
              errors={errors}
              name={"optionC" as any}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
        <input {...register("optionD" as any)} key={"optionD"} type="text" placeholder="Option D" className="input input-bordered" />
       <ErrorMessage
              errors={errors}
              name={"optionD" as any}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
        
  </div>
  {/* <div className="grid grid-cols-2 gap-2">
    {['OptionA', 'OptionB', 'OptionC', 'OptionD'].map(opt => (
      <><input {...register(opt.toLowerCase() as any)} key={opt} type="text" placeholder={opt} className="input input-bordered" />
       <ErrorMessage
              errors={errors}
              name={opt.toLowerCase() as any}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
            </>
    ))}
    
  </div> */}

  {/* Selects */}
  <div className="grid grid-cols-3 gap-2">
   <div className="form-control w-full">
    <label className="label">
      <span className="label-text font-semibold">Answer</span>
    </label>
    <select {...register("answer")} className="select select-bordered w-full" defaultValue="">
      <option value="" disabled>Select Answer</option>
      <option value="1">Option A</option>
      <option value="2">Option B</option>
      <option value="3">Option C</option>
      <option value="4">Option D</option>
    </select>
      <ErrorMessage
              errors={errors}
              name={"answer"}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
  </div>
  <div className="form-control w-full">
    <label className="label">
      <span className="label-text font-semibold">Subject</span>
    </label>
    <select  {...register("subject")} className="select select-bordered w-full" defaultValue="">
      <option value="" disabled>Select Subject</option>
        <option value="1">Tamil</option>
        <option value="2">English</option>
        <option value="3">History & Culture</option>
        <option value="4">Polity</option>
        <option value="5">Geography</option>
        <option value="6">Economy</option>
        <option value="7">Science</option>
        <option value="8">CurrentAffairs</option>
        <option value="9">Aptitude & Mental Ability</option>
    </select>
      <ErrorMessage
              errors={errors}
              name={"subject"}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
  </div>

  <div className="form-control w-full">
    <label className="label">
      <span className="label-text font-semibold">Category</span>
    </label>
    <select  {...register("category")} className="select select-bordered w-full" defaultValue="">
      <option value="" disabled>Select Category</option>
        <option value="101">Repeated Question</option>
        <option value="102">Important Question</option>
        <option value="103">Expected & Current Affairs Questions</option>
        <option value="104">Previous Year Questions</option>
        <option value="100">None</option>
    </select>
      <ErrorMessage
              errors={errors}
              name={"category"}
              render={({ message }) => (
                <p className={Styles.errorText}>{message}</p>
              )}
            />
  </div>
  </div>
  <div className="flex justify-center w-full">
    <button type="submit" className="btn btn-primary w-1/2">{isEditMode ? "Update" : "Submit"}</button>
  </div>

  
</form>

    </>
  );
};
export default AddQus;
